import { useMemo } from "react";
import { Copy, Eye } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/status-badge";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import type { Account } from "../../lib/types";
import { cn, copyText, formatDate, shortText } from "../../lib/utils";

export type ColumnKey =
  | "actions"
  | "account"
  | "emails"
  | "security"
  | "meta"
  | "status"
  | "browser"
  | "secret"
  | "proxy"
  | "updated";

export type AccountGridPreferences = {
  version: number;
  hidden: ColumnKey[];
  order: ColumnKey[];
};

const preferenceStorageKey = "discord-mj-console.account-grid.preferences";
const preferenceVersion = 7;

const baseColumns: Array<{ key: ColumnKey; title: string; className?: string }> = [
  { key: "account", title: "ID / MJ", className: "min-w-[120px]" },
  { key: "emails", title: "当前邮箱 / 新邮箱", className: "min-w-[230px]" },
  { key: "security", title: "密码 / 邮箱密 / 2FA", className: "min-w-[150px]" },
  { key: "meta", title: "批次 / 备注", className: "min-w-[170px]" },
  { key: "status", title: "状态", className: "min-w-[170px]" },
  { key: "secret", title: "Token / Cookie", className: "min-w-[145px]" },
  { key: "proxy", title: "代理", className: "min-w-[220px]" },
  { key: "updated", title: "更新", className: "min-w-[88px]" },
  { key: "browser", title: "Browser Profile", className: "min-w-[210px]" },
  { key: "actions", title: "操作", className: "w-[76px]" }
];

const baseColumnKeys: ColumnKey[] = baseColumns.map((column) => column.key);
const configurableColumnKeys: ColumnKey[] = baseColumnKeys.filter((key) => key !== "actions");
const deprecatedColumnKeys = new Set(["ids", "id", "mjId", "email", "password", "emailPassword", "newEmail", "batch", "remark", "mfa", "login"]);

export const accountGridColumnOptions = baseColumns
  .filter((column) => column.key !== "actions")
  .map((column) => ({ key: column.key, title: column.title }));

export const defaultAccountGridPreferences: AccountGridPreferences = {
  version: preferenceVersion,
  hidden: [],
  order: [...baseColumnKeys]
};

function sanitizeColumnOrder(order: unknown): ColumnKey[] {
  const incoming = Array.isArray(order) ? order : [];
  if (incoming.some((key) => typeof key === "string" && deprecatedColumnKeys.has(key))) {
    return [...baseColumnKeys];
  }
  const seen = new Set<ColumnKey>();
  const next: ColumnKey[] = [];
  for (const key of incoming) {
    if (typeof key !== "string" || !baseColumnKeys.includes(key as ColumnKey) || key === "actions" || seen.has(key as ColumnKey)) continue;
    seen.add(key as ColumnKey);
    next.push(key as ColumnKey);
  }
  for (const key of configurableColumnKeys) {
    if (!seen.has(key)) next.push(key);
  }
  next.push("actions");
  return next;
}

export function normalizeAccountGridPreferences(value: unknown): AccountGridPreferences {
  const source = value && typeof value === "object" ? (value as Partial<AccountGridPreferences>) : {};
  const hidden = Array.isArray(source.hidden)
    ? source.hidden.filter((key): key is ColumnKey => typeof key === "string" && configurableColumnKeys.includes(key as ColumnKey))
    : [];
  return {
    version: preferenceVersion,
    hidden: Array.from(new Set(hidden)),
    order: sanitizeColumnOrder(source.order)
  };
}

export function readStoredGridPreferences(): AccountGridPreferences {
  try {
    const stored = JSON.parse(localStorage.getItem(preferenceStorageKey) || "{}");
    if (!stored || typeof stored !== "object" || (stored as Partial<AccountGridPreferences>).version !== preferenceVersion) {
      return defaultAccountGridPreferences;
    }
    return normalizeAccountGridPreferences(stored);
  } catch {
    return defaultAccountGridPreferences;
  }
}

export function storeGridPreferences(preferences: AccountGridPreferences) {
  try {
    localStorage.setItem(preferenceStorageKey, JSON.stringify(normalizeAccountGridPreferences(preferences)));
  } catch {
    // Local table preferences are non-critical.
  }
}

function secretPreview(value: unknown, mode: "short" | "present" = "short"): string {
  const text = String(value ?? "");
  if (!text) return "-";
  if (mode === "present") return "已写入";
  if (text.length <= 4) return "已填写";
  return `${text.slice(0, 2)}...${text.slice(-2)}`;
}

function pickProxyField(proxy: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = proxy[key];
    if (value !== null && value !== undefined && String(value).trim()) return String(value).trim();
  }
  return "";
}

function parseProxy(value: unknown): Record<string, unknown> | null {
  if (!value) return null;
  if (typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return null;
  if (!/^[{[]/.test(text)) return { proxy_summary: text };
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : { proxy_summary: text };
  } catch {
    return { proxy_summary: text };
  }
}

function maskProxyUrl(value: string): string {
  const text = value.trim();
  if (!text) return "-";
  try {
    const parsed = new URL(text);
    if (parsed.password) parsed.password = "***";
    return parsed.toString();
  } catch {
    return text.length > 80 ? `${text.slice(0, 44)}...${text.slice(-18)}` : text;
  }
}

function warningText(account: Account) {
  if (!account.warning_enabled) return "";
  return account.warning_message || "已开启警告";
}

function copyValue(label: string, value: unknown) {
  const text = String(value ?? "");
  if (!text) {
    toast.info(`${label}为空`);
    return;
  }
  copyText(text);
  toast.success(`${label}已复制`);
}

function HoverText({
  value,
  display,
  copyLabel,
  showTooltip = true,
  className,
  mono = false,
  muted = false
}: {
  value: unknown;
  display?: unknown;
  copyLabel?: string;
  showTooltip?: boolean;
  className?: string;
  mono?: boolean;
  muted?: boolean;
}) {
  const text = String(value ?? "");
  const displayText = String(display ?? text);
  const canCopy = Boolean(copyLabel && text && text !== "-");
  const content = (
    <span
      className={cn(
        "block truncate text-xs",
        canCopy && "cursor-copy hover:underline",
        mono && "font-mono",
        muted && "text-muted-foreground",
        className
      )}
      onClick={(event) => {
        if (!canCopy) return;
        event.stopPropagation();
        copyValue(copyLabel || "内容", text);
      }}
    >
      {displayText || "-"}
    </span>
  );
  if (!showTooltip || !text || text === "-") return content;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent className="max-w-[520px] whitespace-normal break-all leading-5">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

function DataText({ value, display, copyLabel, mono = false, muted = false }: { value: unknown; display?: unknown; copyLabel?: string; mono?: boolean; muted?: boolean }) {
  return (
    <HoverText value={value} display={display} copyLabel={copyLabel} className="max-w-[280px]" mono={mono} muted={muted} />
  );
}

function StackValue({ value, display, copyLabel, mono = false, muted = false }: { value: unknown; display?: unknown; copyLabel?: string; mono?: boolean; muted?: boolean }) {
  return (
    <HoverText value={value} display={display} copyLabel={copyLabel} mono={mono} muted={muted} />
  );
}

function AccountSummary({ account }: { account: Account }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <StackValue value={account.id} copyLabel="ID" mono />
      <StackValue value={account.mj_id} copyLabel="MJ ID" mono />
    </div>
  );
}

function EmailSummary({ account }: { account: Account }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <StackValue value={account.current_email} copyLabel="当前邮箱" />
      {account.new_email ? <StackValue value={account.new_email} copyLabel="新邮箱" /> : <StackValue value="-" muted />}
    </div>
  );
}

function SecuritySummary({ account }: { account: Account }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <StackValue value={account.password} display={secretPreview(account.password)} copyLabel="密码" mono />
      <StackValue value={account.current_email_password} display={secretPreview(account.current_email_password)} copyLabel="邮箱密" mono />
      <StackValue value={account.mfa_secret} display={secretPreview(account.mfa_secret)} copyLabel="2FA" mono />
    </div>
  );
}

function MetaSummary({ account }: { account: Account }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <StackValue value={account.batch_no} copyLabel="批次" />
      <StackValue value={account.remark} copyLabel="备注" muted />
    </div>
  );
}

function SecretSummary({ account }: { account: Account }) {
  return (
    <div className="flex flex-col gap-1 text-xs">
      <button className="flex max-w-[150px] items-center gap-1 truncate text-left font-mono hover:underline [&_svg]:size-3 [&_svg]:shrink-0" type="button" onClick={() => copyValue("Token", account.token)}>
        <Copy data-icon="inline-start" />
        <HoverText value={account.token} display={`Token ${shortText(account.token, 6) || "-"}`} copyLabel="Token" showTooltip={false} />
      </button>
      <button className="flex max-w-[150px] items-center gap-1 truncate text-left hover:underline [&_svg]:size-3 [&_svg]:shrink-0" type="button" onClick={() => copyValue("Cookie", account.mj_cookie)}>
        <Copy data-icon="inline-start" />
        <HoverText value={account.mj_cookie} display={`Cookie ${account.mj_cookie ? "已写入" : "-"}`} copyLabel="Cookie" showTooltip={false} />
      </button>
    </div>
  );
}

function ProxySummary({ account }: { account: Account }) {
  const proxy = parseProxy(account.current_s5_proxy);
  if (!proxy) return <DataText value="-" muted />;
  const summary = pickProxyField(proxy, ["proxy_summary", "summary", "safe_summary", "proxy_url", "url"]);
  const exitIp = pickProxyField(proxy, ["exit_ip", "ip", "public_ip"]);
  const host = pickProxyField(proxy, ["proxy_host", "host", "server"]);
  const port = pickProxyField(proxy, ["proxy_port", "port"]);
  const endpoint = host && port ? `${host}:${port}` : summary;
  const raw = typeof account.current_s5_proxy === "string" ? account.current_s5_proxy : summary || endpoint;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <StackValue value={raw || "-"} display={maskProxyUrl(endpoint || raw || "-")} copyLabel="代理链接" mono />
      <StackValue value={exitIp || "-"} display={`出口 ${exitIp || "-"}`} copyLabel="出口 IP" mono muted={!exitIp} />
      <span className="text-[11px] text-muted-foreground">获取 {formatDate(account.current_s5_proxy_fetched_at)}</span>
    </div>
  );
}

function StatusSummary({ account }: { account: Account }) {
  const status = account.deleted_at ? "deleted" : account.latest_task?.status || "idle";
  const task = [account.latest_task?.task_type, account.latest_task?.execute_message].filter(Boolean).join(" ");
  const warning = warningText(account);
  const showActive =
    account.active_task &&
    (account.active_task.id !== account.latest_task?.id || account.active_task.status !== status);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1">
        <StatusBadge status={status} />
        {showActive ? <StatusBadge status={account.active_task?.status} /> : null}
      </div>
      {task ? <DataText value={task} copyLabel="任务信息" muted /> : null}
      {warning ? <HoverText value={warning} copyLabel="警告信息" className="max-w-[180px] rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700" /> : null}
    </div>
  );
}

function BrowserSummary({ account }: { account: Account }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="flex items-center gap-1">
        <StackValue value={account.browser_type || "cloakbrowser"} copyLabel="Browser Type" />
      </div>
      <StackValue value={account.browser_profile_id || "-"} copyLabel="Browser Profile ID" mono />
      <StackValue value={account.browser_manager_id || "-"} copyLabel="Browser Manager ID" muted />
    </div>
  );
}

function AccountActions({ account, onSelect }: { account: Account; onSelect: (account: Account) => void }) {
  return (
    <div className="flex items-center gap-1" onClick={(event) => event.stopPropagation()}>
      <Button className="h-7 px-2" onClick={() => onSelect(account)}>
        <Eye data-icon="inline-start" />
        编辑
      </Button>
    </div>
  );
}

function cellContent(account: Account, key: ColumnKey) {
  if (key === "actions") return null;
  if (key === "account") return <AccountSummary account={account} />;
  if (key === "emails") return <EmailSummary account={account} />;
  if (key === "security") return <SecuritySummary account={account} />;
  if (key === "meta") return <MetaSummary account={account} />;
  if (key === "status") return <StatusSummary account={account} />;
  if (key === "browser") return <BrowserSummary account={account} />;
  if (key === "secret") return <SecretSummary account={account} />;
  if (key === "proxy") return <ProxySummary account={account} />;
  return <DataText value={formatDate(account.updated_at)} muted />;
}

export function AccountDataGrid({
  accounts,
  onSelect,
  selectedIds,
  onSelectedIdsChange,
  preferences = defaultAccountGridPreferences
}: {
  accounts: Account[];
  onSelect: (account: Account) => void;
  selectedIds: Set<number>;
  onSelectedIdsChange: (ids: Set<number>) => void;
  preferences?: AccountGridPreferences;
}) {
  const normalizedPreferences = useMemo(() => normalizeAccountGridPreferences(preferences), [preferences]);
  const columns = useMemo(
    () =>
      normalizedPreferences.order
        .filter((key) => key === "actions" || !normalizedPreferences.hidden.includes(key))
        .map((key) => baseColumns.find((item) => item.key === key)!)
        .filter(Boolean),
    [normalizedPreferences]
  );

  const allSelected = accounts.length > 0 && accounts.every((account) => selectedIds.has(account.id));
  const someSelected = accounts.some((account) => selectedIds.has(account.id));

  function setRowSelected(account: Account, checked: boolean) {
    const next = new Set(selectedIds);
    if (checked) next.add(account.id);
    else next.delete(account.id);
    onSelectedIdsChange(next);
  }

  function toggleRowSelected(account: Account) {
    setRowSelected(account, !selectedIds.has(account.id));
  }

  function setPageSelected(checked: boolean) {
    const next = new Set(selectedIds);
    for (const account of accounts) {
      if (checked) next.add(account.id);
      else next.delete(account.id);
    }
    onSelectedIdsChange(next);
  }

  function stickyActionClass(key: ColumnKey) {
    return key === "actions" ? "sticky right-0 z-20 border-l bg-background shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.45)]" : "";
  }

  return (
    <TooltipProvider delayDuration={200}>
    <div className="h-full min-h-0 overflow-hidden rounded-md border border-border bg-background">
      <div className="h-full overflow-auto">
        <Table className="min-w-max text-xs">
          <TableHeader className="sticky top-0 z-30 bg-background shadow-sm">
            <TableRow>
              <TableHead className="h-8 w-9 px-2">
                <Checkbox checked={allSelected ? true : someSelected ? "indeterminate" : false} onCheckedChange={(checked) => setPageSelected(checked === true)} aria-label="选择本页账号" />
              </TableHead>
              {columns.map((column) => (
                <TableHead key={column.key} className={cn("h-8 whitespace-nowrap px-2", column.className, stickyActionClass(column.key))}>
                  {column.title}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.length ? (
              accounts.map((account) => (
                <TableRow
                  key={account.id}
                  data-state={selectedIds.has(account.id) ? "selected" : undefined}
                  className={cn("cursor-pointer", account.deleted_at && "opacity-60")}
                  onClick={() => toggleRowSelected(account)}
                >
                  <TableCell className="w-9 px-2 py-1.5" onClick={(event) => event.stopPropagation()}>
                    <Checkbox checked={selectedIds.has(account.id)} onCheckedChange={(checked) => setRowSelected(account, checked === true)} aria-label={`选择账号 ${account.id}`} />
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell key={`${account.id}:${column.key}`} className={cn("h-11 max-w-[300px] whitespace-nowrap px-2 py-1.5 align-top", column.className, stickyActionClass(column.key))}>
                      {column.key === "actions" ? <AccountActions account={account} onSelect={onSelect} /> : cellContent(account, column.key)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} className="h-40 text-center text-muted-foreground">
                  暂无账号数据
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
    </TooltipProvider>
  );
}
